import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertTriangle, ShieldAlert, Lock, Loader2, RefreshCw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const statusBadgeClass = {
  sent: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  blocked: 'bg-amber-100 text-amber-700 border-amber-200',
};

const AdminEmailIncidentDashboard = () => {
  const [windowKey, setWindowKey] = useState('24h');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lockdownLoading, setLockdownLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({
    email_enabled: true,
    emergency_lockdown: false,
    global_per_hour_limit: 500,
    per_recipient_per_hour_limit: 6,
    max_distinct_recipients_per_fingerprint_hour: 40,
    auto_lockdown_enabled: true,
    auto_lockdown_blocked_threshold: 50,
    auto_lockdown_window_minutes: 10,
    ip_protection_enabled: true,
    auto_block_repeated_ip_enabled: true,
    repeated_ip_block_threshold: 8,
    repeated_ip_block_window_minutes: 10,
    auto_block_out_of_country_enabled: true,
    allowed_countries: ['US'],
  });
  const [allowedCountriesInput, setAllowedCountriesInput] = useState('US');
  const [blockedIpsData, setBlockedIpsData] = useState({ blocked_ips: [], total: 0 });
  const [blockedIpLoading, setBlockedIpLoading] = useState(false);
  const [blockedIpFilter, setBlockedIpFilter] = useState('blocked');
  const [blockedIpSearch, setBlockedIpSearch] = useState('');

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDashboard = async (spinner = false) => {
    if (spinner) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/email-security/incident-dashboard`, {
        headers: authHeaders(),
        params: {
          window: windowKey,
          status: statusFilter,
          search,
          limit: PAGE_SIZE,
          skip,
        },
      });
      setData(response.data);
      setSettings((prev) => ({ ...prev, ...(response.data?.security_settings || {}) }));
      const countries = response.data?.security_settings?.allowed_countries;
      if (Array.isArray(countries)) {
        setAllowedCountriesInput(countries.join(', '));
      }
    } catch (error) {
      toast({
        title: 'Failed to load incident dashboard',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchBlockedIps = async () => {
    setBlockedIpLoading(true);
    try {
      const response = await axios.get(`${API}/settings/email-security/blocked-ips`, {
        headers: authHeaders(),
        params: {
          status: blockedIpFilter,
          search: blockedIpSearch,
          limit: 100,
          skip: 0,
        },
      });
      setBlockedIpsData(response.data || { blocked_ips: [], total: 0 });
    } catch (error) {
      toast({
        title: 'Failed to load blocked IP list',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBlockedIpLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(false);
  }, [windowKey, statusFilter, skip]);

  useEffect(() => {
    fetchBlockedIps();
  }, [blockedIpFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBlockedIps();
    }, 300);
    return () => clearTimeout(timer);
  }, [blockedIpSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSkip(0);
      fetchDashboard(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const summary = data?.summary || { sent: 0, failed: 0, blocked: 0, total: 0 };
  const logs = data?.logs || [];
  const totalLogs = data?.total_logs || 0;

  const onSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const countries = allowedCountriesInput
        .split(',')
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);

      await axios.post(
        `${API}/settings/email-security`,
        { ...settings, allowed_countries: countries.length ? countries : ['US'] },
        { headers: authHeaders() },
      );
      toast({ title: 'Saved', description: 'Email security settings updated.' });
      fetchDashboard(true);
      fetchBlockedIps();
    } catch (error) {
      toast({ title: 'Save failed', description: error.response?.data?.detail || 'Unable to save settings', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleBlockedIp = async (ip, isBlocked) => {
    try {
      await axios.post(
        `${API}/settings/email-security/blocked-ips/toggle`,
        {
          ip,
          is_blocked: isBlocked,
          reason: isBlocked ? 'manual_block' : 'manual_unblock',
        },
        { headers: authHeaders() },
      );
      toast({ title: 'Updated', description: `IP ${ip} is now ${isBlocked ? 'blocked' : 'unblocked'}.` });
      fetchBlockedIps();
    } catch (error) {
      toast({ title: 'Update failed', description: error.response?.data?.detail || 'Unable to update blocked IP', variant: 'destructive' });
    }
  };

  const onEnableLockdown = async () => {
    try {
      setLockdownLoading(true);
      await axios.post(`${API}/settings/email-security/lockdown`, {}, { headers: authHeaders() });
      toast({ title: 'Emergency lockdown enabled', description: 'Outbound email has been disabled immediately.' });
      fetchDashboard(true);
    } catch (error) {
      toast({ title: 'Lockdown failed', description: error.response?.data?.detail || 'Unable to enable lockdown', variant: 'destructive' });
    } finally {
      setLockdownLoading(false);
    }
  };

  const lockdownActive = useMemo(() => !settings.email_enabled || settings.emergency_lockdown, [settings]);

  return (
    <div className="space-y-6" data-testid="admin-email-incident-dashboard-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="email-incident-dashboard-title">
            <ShieldAlert className="w-6 h-6 text-[#6e2ea8]" />
            Email Incident Dashboard
          </h1>
          <p className="text-gray-500" data-testid="email-incident-dashboard-subtitle">Monitor abuse patterns, blocked sends, and trigger immediate lockdown.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => fetchDashboard(true)} data-testid="email-incident-refresh-button">
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Refresh
          </Button>
          <Button onClick={onEnableLockdown} disabled={lockdownLoading} className="bg-red-600 hover:bg-red-700" data-testid="email-incident-lockdown-button">
            <Lock className="w-4 h-4 mr-2" />
            Enable Lockdown
          </Button>
        </div>
      </div>

      <Card data-testid="email-incident-status-card">
        <CardContent className="pt-6 flex flex-wrap items-center gap-2">
          <Badge className={lockdownActive ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700 border border-green-200'} data-testid="email-incident-lockdown-status-badge">
            {lockdownActive ? 'LOCKDOWN ACTIVE' : 'EMAIL FLOW ENABLED'}
          </Badge>
          <Badge className="bg-slate-100 text-slate-700 border border-slate-200" data-testid="email-incident-window-badge">Window: {windowKey}</Badge>
          <Badge className="bg-blue-100 text-blue-700 border border-blue-200" data-testid="email-incident-total-badge">Total events: {summary.total}</Badge>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="email-incident-summary-total"><CardHeader><CardDescription>Total</CardDescription><CardTitle>{summary.total}</CardTitle></CardHeader></Card>
        <Card data-testid="email-incident-summary-sent"><CardHeader><CardDescription>Sent</CardDescription><CardTitle>{summary.sent}</CardTitle></CardHeader></Card>
        <Card data-testid="email-incident-summary-failed"><CardHeader><CardDescription>Failed</CardDescription><CardTitle>{summary.failed}</CardTitle></CardHeader></Card>
        <Card data-testid="email-incident-summary-blocked"><CardHeader><CardDescription>Blocked</CardDescription><CardTitle>{summary.blocked}</CardTitle></CardHeader></Card>
      </div>

      <Card data-testid="email-incident-controls-card">
        <CardHeader>
          <CardTitle>Security Controls</CardTitle>
          <CardDescription>Admin + Super Admin can adjust these controls.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>Global per hour</Label>
            <Input type="number" value={settings.global_per_hour_limit} onChange={(e) => setSettings((prev) => ({ ...prev, global_per_hour_limit: Number(e.target.value || 0) }))} data-testid="email-incident-global-limit-input" />
          </div>
          <div className="space-y-2">
            <Label>Per recipient/hour</Label>
            <Input type="number" value={settings.per_recipient_per_hour_limit} onChange={(e) => setSettings((prev) => ({ ...prev, per_recipient_per_hour_limit: Number(e.target.value || 0) }))} data-testid="email-incident-per-recipient-limit-input" />
          </div>
          <div className="space-y-2">
            <Label>Distinct recipients/fingerprint/hour</Label>
            <Input type="number" value={settings.max_distinct_recipients_per_fingerprint_hour} onChange={(e) => setSettings((prev) => ({ ...prev, max_distinct_recipients_per_fingerprint_hour: Number(e.target.value || 0) }))} data-testid="email-incident-fingerprint-limit-input" />
          </div>
          <div className="space-y-2">
            <Label>Auto-lockdown blocked threshold</Label>
            <Input type="number" value={settings.auto_lockdown_blocked_threshold} onChange={(e) => setSettings((prev) => ({ ...prev, auto_lockdown_blocked_threshold: Number(e.target.value || 0) }))} data-testid="email-incident-auto-threshold-input" />
          </div>

          <div className="space-y-2">
            <Label>Auto-lockdown window (minutes)</Label>
            <Input type="number" value={settings.auto_lockdown_window_minutes} onChange={(e) => setSettings((prev) => ({ ...prev, auto_lockdown_window_minutes: Number(e.target.value || 0) }))} data-testid="email-incident-auto-window-input" />
          </div>
          <div className="space-y-2">
            <Label>IP protection</Label>
            <Select value={settings.ip_protection_enabled ? 'true' : 'false'} onValueChange={(value) => setSettings((prev) => ({ ...prev, ip_protection_enabled: value === 'true' }))}>
              <SelectTrigger data-testid="email-incident-ip-protection-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Auto-block repeated IP</Label>
            <Select value={settings.auto_block_repeated_ip_enabled ? 'true' : 'false'} onValueChange={(value) => setSettings((prev) => ({ ...prev, auto_block_repeated_ip_enabled: value === 'true' }))}>
              <SelectTrigger data-testid="email-incident-auto-block-repeat-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Repeated IP threshold</Label>
            <Input type="number" value={settings.repeated_ip_block_threshold} onChange={(e) => setSettings((prev) => ({ ...prev, repeated_ip_block_threshold: Number(e.target.value || 0) }))} data-testid="email-incident-repeated-ip-threshold-input" />
          </div>
          <div className="space-y-2">
            <Label>Repeated IP window (minutes)</Label>
            <Input type="number" value={settings.repeated_ip_block_window_minutes} onChange={(e) => setSettings((prev) => ({ ...prev, repeated_ip_block_window_minutes: Number(e.target.value || 0) }))} data-testid="email-incident-repeated-ip-window-input" />
          </div>
          <div className="space-y-2">
            <Label>Auto-block out-of-country</Label>
            <Select value={settings.auto_block_out_of_country_enabled ? 'true' : 'false'} onValueChange={(value) => setSettings((prev) => ({ ...prev, auto_block_out_of_country_enabled: value === 'true' }))}>
              <SelectTrigger data-testid="email-incident-out-of-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2 xl:col-span-2">
            <Label>Allowed countries (comma separated ISO codes)</Label>
            <Input value={allowedCountriesInput} onChange={(e) => setAllowedCountriesInput(e.target.value)} placeholder="US, CA" data-testid="email-incident-allowed-countries-input" />
          </div>
          <div className="space-y-2">
            <Label>Email enabled</Label>
            <Select value={settings.email_enabled ? 'true' : 'false'} onValueChange={(value) => setSettings((prev) => ({ ...prev, email_enabled: value === 'true' }))}>
              <SelectTrigger data-testid="email-incident-enabled-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Auto-lockdown</Label>
            <Select value={settings.auto_lockdown_enabled ? 'true' : 'false'} onValueChange={(value) => setSettings((prev) => ({ ...prev, auto_lockdown_enabled: value === 'true' }))}>
              <SelectTrigger data-testid="email-incident-auto-lockdown-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={onSaveSettings} disabled={savingSettings} className="w-full bg-[#6e2ea8] hover:bg-[#5a238c]" data-testid="email-incident-save-settings-button">
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Controls
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card data-testid="email-incident-top-fingerprints-card">
          <CardHeader><CardTitle>Top Fingerprints</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data?.top_fingerprints || []).map((item, idx) => (
                <div key={item.fingerprint || idx} className="text-sm border rounded p-2" data-testid={`email-incident-fingerprint-row-${idx}`}>
                  <div className="font-medium truncate">{item.fingerprint}</div>
                  <div className="text-xs text-gray-500">Count: {item.count} · Last seen: {item.last_seen ? new Date(item.last_seen).toLocaleString() : '—'}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="email-incident-top-domains-card">
          <CardHeader><CardTitle>Top Recipient Domains</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data?.top_domains || []).map((item, idx) => (
                <div key={item.domain || idx} className="text-sm border rounded p-2 flex items-center justify-between" data-testid={`email-incident-domain-row-${idx}`}>
                  <span>{item.domain}</span>
                  <Badge className="bg-slate-100 text-slate-700 border border-slate-200">{item.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="email-incident-blocked-ip-card">
        <CardHeader>
          <CardTitle>Blocked IP Manager</CardTitle>
          <CardDescription>Unblock accidental hits or manually block known bad sources.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={blockedIpFilter} onValueChange={setBlockedIpFilter}>
                <SelectTrigger data-testid="email-incident-blocked-ip-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="unblocked">Unblocked</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Search</Label>
              <Input value={blockedIpSearch} onChange={(e) => setBlockedIpSearch(e.target.value)} placeholder="IP / country / reason" data-testid="email-incident-blocked-ip-search-input" />
            </div>
          </div>

          {blockedIpLoading ? (
            <div className="h-24 flex items-center justify-center" data-testid="email-incident-blocked-ip-loading-state"><Loader2 className="w-5 h-5 animate-spin text-[#6e2ea8]" /></div>
          ) : (
            <div className="overflow-auto" data-testid="email-incident-blocked-ip-table-wrapper">
              <table className="min-w-full text-sm" data-testid="email-incident-blocked-ip-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">IP</th>
                    <th className="text-left p-2">Country</th>
                    <th className="text-left p-2">Reason</th>
                    <th className="text-left p-2">State</th>
                    <th className="text-left p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(blockedIpsData.blocked_ips || []).map((row) => (
                    <tr key={row.id || row.ip} className="border-t" data-testid={`email-incident-blocked-ip-row-${(row.ip || '').replace(/[^a-zA-Z0-9]/g, '-')}`}>
                      <td className="p-2 text-xs">{row.ip}</td>
                      <td className="p-2 text-xs">{row.country || 'UNKNOWN'}</td>
                      <td className="p-2 text-xs">{row.reason || '—'}</td>
                      <td className="p-2 text-xs">{row.is_blocked ? 'Blocked' : 'Unblocked'}</td>
                      <td className="p-2">
                        <Button variant="outline" size="sm" onClick={() => toggleBlockedIp(row.ip, !row.is_blocked)} data-testid={`email-incident-blocked-ip-toggle-${(row.ip || '').replace(/[^a-zA-Z0-9]/g, '-')}`}>
                          {row.is_blocked ? 'Unblock' : 'Block'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="email-incident-log-table-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" /> Email Event Log</CardTitle>
          <CardDescription>Filter and inspect blocked/failed/sent attempts.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="space-y-1">
              <Label>Window</Label>
              <Select value={windowKey} onValueChange={(value) => { setSkip(0); setWindowKey(value); }}>
                <SelectTrigger data-testid="email-incident-window-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">Last 1 hour</SelectItem>
                  <SelectItem value="24h">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={(value) => { setSkip(0); setStatusFilter(value); }}>
                <SelectTrigger data-testid="email-incident-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Search</Label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="email / subject / event" data-testid="email-incident-search-input" />
            </div>
          </div>

          {loading ? (
            <div className="h-40 flex items-center justify-center" data-testid="email-incident-loading-state"><Loader2 className="w-6 h-6 animate-spin text-[#6e2ea8]" /></div>
          ) : (
            <div className="overflow-auto" data-testid="email-incident-table-wrapper">
              <table className="min-w-full text-sm" data-testid="email-incident-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Time</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Recipient</th>
                    <th className="text-left p-2">Event</th>
                    <th className="text-left p-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((row) => (
                    <tr key={row.id} className="border-t" data-testid={`email-incident-log-row-${row.id}`}>
                      <td className="p-2 text-xs text-gray-600">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                      <td className="p-2"><span className={`inline-flex px-2 py-1 rounded border text-xs ${statusBadgeClass[row.status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>{row.status}</span></td>
                      <td className="p-2 text-xs">{row.to_email || '—'}</td>
                      <td className="p-2 text-xs">{row.event_type || '—'}</td>
                      <td className="p-2 text-xs">
                        <div>{row.message || '—'}</div>
                        {row.error ? <div className="text-red-600 mt-1">{row.error}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-gray-500" data-testid="email-incident-pagination-text">
              Showing {logs.length === 0 ? 0 : skip + 1}–{Math.min(skip + logs.length, totalLogs)} of {totalLogs}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setSkip((prev) => Math.max(0, prev - PAGE_SIZE))} disabled={skip === 0} data-testid="email-incident-prev-page-button">Previous</Button>
              <Button variant="outline" onClick={() => setSkip((prev) => prev + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= totalLogs} data-testid="email-incident-next-page-button">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailIncidentDashboard;
